import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import TextInputField from 'components/TextInputField';
import MagicButton from 'components/MagicButton/MagicButton';
import StatusMessage from 'components/StatusMessage';
import CountrySelect from 'components/CountrySelect';
import { useGetProfileQuery, useUpdateProfileMutation, useGetEmailChangeStatusQuery, useResendVerificationMutation } from 'store/services/auth/authApi';
import { useGetMeQuery, useUpdateAnimatedBorderMutation, useUpdateTradingPreferencesMutation } from 'store/services/user/meApi';
import { EliteUpsellPrompt } from 'components/EliteUpsellPrompt/EliteUpsellPrompt';
import { useGetNotificationSettingsQuery, useUpdateNotificationSettingsMutation } from 'store/services/message/messageApi';
import { useGetWantListDefaultsQuery, useUpdateWantListDefaultsMutation, useGetHaveListDefaultsQuery, useUpdateHaveListDefaultsMutation, useGetFeedPreferencesQuery, useUpdateFeedPreferencesMutation } from 'store/services/settings/settingsApi';
import { useGetProfileSettingsQuery, useUpdateProfileSettingsMutation, useGetUserSocialLinksQuery, useSaveSocialLinksMutation } from 'store/services/profile/profileApi';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import EmailChangeModal from 'components/EmailChangeModal/EmailChangeModal';
import MobileVerificationModal from 'components/MobileVerificationModal/MobileVerificationModal';
import BorderColorPickerModal from 'components/BorderColorPickerModal/BorderColorPickerModal';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import AvatarBorderPreviewBanner from './components/AvatarBorderPreviewBanner/AvatarBorderPreviewBanner';
import ToggleSwitch from 'components/ToggleSwitch';
import InlineFoilPills from 'components/InlineFoilPills/InlineFoilPills';
import InlineConditionPills from 'components/InlineConditionPills/InlineConditionPills';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AssetUrls } from 'config/assets';
import { toast } from 'react-toastify';
import { containsUrl } from 'utils/urlDetection';
import { detectPlatform, PLATFORM_ICON_MAP, PLATFORM_LABEL_MAP } from 'utils/socialPlatformDetect';
import BillingTab from './components/BillingTab/BillingTab';
import CustomEmojiTab from './components/CustomEmojiTab/CustomEmojiTab';
import { SUPPORTED_LANGUAGES } from 'constants/languages';
import './Settings.css';

const LANGUAGES = SUPPORTED_LANGUAGES.map(l => ({ code: l.code, name: l.displayName }));

const PreferenceSection: React.FC<{ title: string; description?: string; children: React.ReactNode }> = ({ title, description, children }) => (
  <div className="settings-preference-card">
    <h3 className="settings-preference-card__title">{title}</h3>
    {description && <p className="settings-preference-card__desc">{description}</p>}
    {children}
  </div>
);

export default function Settings() {
  type SettingsTab = 'Profile' | 'Public' | 'Preferences' | 'Email' | 'Emoji' | 'Billing';
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    (location.state as { tab?: SettingsTab })?.tab || 'Public'
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLanguageExpanded, setIsLanguageExpanded] = useState(false);

  const TAB_GROUPS = [
    { label: 'Account', tabs: [
      { key: 'Public' as SettingsTab, label: 'Public Profile' },
      { key: 'Profile' as SettingsTab, label: 'Account' },
    ]},
    { label: 'Preferences', tabs: [
      { key: 'Preferences' as SettingsTab, label: 'Preferences' },
      { key: 'Email' as SettingsTab, label: 'Email' },
      { key: 'Emoji' as SettingsTab, label: 'Custom Emoji' },
    ]},
    { label: 'Billing', tabs: [
      { key: 'Billing' as SettingsTab, label: 'Billing' },
    ]},
  ];

  const MOBILE_TABS: { key: SettingsTab; label: string }[] = [
    { key: 'Public', label: 'Public' },
    { key: 'Profile', label: 'Account' },
    { key: 'Preferences', label: 'Prefs' },
    { key: 'Email', label: 'Email' },
    { key: 'Emoji', label: 'Emoji' },
    { key: 'Billing', label: 'Billing' },
  ];

  const [form, setForm] = useState({
    username: '',
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    email: '',
    phone: '',
  });

  const [originalForm, setOriginalForm] = useState({
    username: '',
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    email: '',
    phone: '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isBorderColorModalOpen, setIsBorderColorModalOpen] = useState(false);
  const [hasSavedBorder, setHasSavedBorder] = useState(false);

  // API hooks
  const { data: profileData, isLoading: isLoadingProfile, error: profileError } = useGetProfileQuery();
  const [updateProfile, { isLoading: isSaving }] = useUpdateProfileMutation();
  const { data: emailChangeStatus, refetch: refetchEmailStatus } = useGetEmailChangeStatusQuery();
  const { data: userData } = useGetMeQuery();
  const [updateAnimatedBorder] = useUpdateAnimatedBorderMutation();
  const [updateTradingPreferences, { isLoading: isUpdatingTradingPrefs }] = useUpdateTradingPreferencesMutation();
  const [showDomesticTradesUpsell, setShowDomesticTradesUpsell] = useState(false);
  const [showEliteProfileUpsell, setShowEliteProfileUpsell] = useState(false);
  const [resendVerification] = useResendVerificationMutation();
  const { data: notificationSettings, isLoading: isLoadingNotifications } = useGetNotificationSettingsQuery();
  const [updateNotificationSettings, { isLoading: isUpdatingNotifications }] = useUpdateNotificationSettingsMutation();
  const { data: wantListDefaults, isLoading: isLoadingWantListDefaults } = useGetWantListDefaultsQuery();
  const [updateWantListDefaults, { isLoading: isUpdatingWantListDefaults }] = useUpdateWantListDefaultsMutation();
  const { data: profileSettings, isLoading: isLoadingProfileSettings } = useGetProfileSettingsQuery();
  const [updateProfileSettings, { isLoading: isUpdatingProfileSettings }] = useUpdateProfileSettingsMutation();
  const { data: haveListDefaults, isLoading: isLoadingHaveListDefaults } = useGetHaveListDefaultsQuery();
  const [updateHaveListDefaults, { isLoading: isUpdatingHaveListDefaults }] = useUpdateHaveListDefaultsMutation();
  const { data: feedPreferences, isLoading: isLoadingFeedPreferences } = useGetFeedPreferencesQuery();
  const [updateFeedPreferences, { isLoading: isUpdatingFeedPreferences }] = useUpdateFeedPreferencesMutation();
  const [bio, setBio] = useState('');
  const [bioChanged, setBioChanged] = useState(false);
  const [bioHasUrl, setBioHasUrl] = useState(false);

  // Social links state
  const { data: socialLinksData } = useGetUserSocialLinksQuery(userData?.username || '', { skip: !userData?.username });
  const [saveSocialLinks, { isLoading: isSavingLinks }] = useSaveSocialLinksMutation();
  const [socialRows, setSocialRows] = useState<Array<{ url: string; error: string }>>([{ url: '', error: '' }]);
  const [socialLinksChanged, setSocialLinksChanged] = useState(false);

  // Hydrate hasSavedBorder flag from localStorage once userId is known
  useEffect(() => {
    if (userData?.id != null) {
      const key = `avatarBorderPreview.hasSaved.${userData.id}`;
      setHasSavedBorder(localStorage.getItem(key) === 'true');
    }
  }, [userData?.id]);

  // Load social links from API
  useEffect(() => {
    if (socialLinksData) {
      if (socialLinksData.links.length > 0) {
        setSocialRows(socialLinksData.links.map((l) => ({ url: l.url, error: '' })));
      } else {
        setSocialRows([{ url: '', error: '' }]);
      }
      setSocialLinksChanged(false);
    }
  }, [socialLinksData]);

  // Load bio from profile settings
  useEffect(() => {
    if (profileSettings?.bio !== undefined) {
      setBio(profileSettings.bio || '');
      setBioChanged(false);
    }
  }, [profileSettings?.bio]);

  // Load profile data on component mount
  useEffect(() => {
    if (profileData) {
      console.log('📍 Profile data received:', profileData);
      const formData = {
        username: profileData.username || '',
        // Check for nested shippingAddress object (new structure) or flat fields (old structure)
        firstName: profileData.shippingAddress?.firstName || profileData.shippingFirstName || '',
        lastName: profileData.shippingAddress?.lastName || profileData.shippingLastName || '',
        address1: profileData.shippingAddress?.address1 || profileData.shippingAddress1 || '',
        address2: profileData.shippingAddress?.address2 || profileData.shippingAddress2 || '',
        city: profileData.shippingAddress?.city || profileData.shippingCity || '',
        state: profileData.shippingAddress?.state || profileData.shippingState || '',
        zip: profileData.shippingAddress?.postalCode || profileData.shippingPostalCode || '',
        country: profileData.shippingAddress?.country || profileData.shippingCountry || '',
        email: profileData.email || '',
        phone: profileData.phoneNumber || profileData.phone || '',
      };
      console.log('📍 Form data mapped:', formData);
      setForm(formData);
      setOriginalForm(formData);
    }
  }, [profileData]);

  // Refetch email change status when component mounts or when profile data changes
  useEffect(() => {
    refetchEmailStatus();
  }, [profileData, refetchEmailStatus]);


  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }

    // Clear save status when user makes changes
    setSaveStatus(null);
  };

  // Check if form has changes
  const hasChanges = () => {
    return JSON.stringify(form) !== JSON.stringify(originalForm);
  };

  // Validate required fields
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!form.username.trim()) errors.username = 'Username is required';
    if (!form.firstName.trim()) errors.firstName = 'First name is required';
    if (!form.lastName.trim()) errors.lastName = 'Last name is required';
    if (!form.address1.trim()) errors.address1 = 'Address line 1 is required';
    if (!form.city.trim()) errors.city = 'City is required';
    if (!form.state.trim()) errors.state = 'State/Province is required';
    if (!form.zip.trim()) errors.zip = 'ZIP/Postal code is required';
    if (!form.country) errors.country = 'Country is required';
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setSaveStatus(null);
  };

  const handleCancel = () => {
    // Reset form to original values
    setForm(originalForm);
    setIsEditMode(false);
    setValidationErrors({});
    setSaveStatus(null);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setSaveStatus({ type: 'error', message: 'Please fix the errors above before saving.' });
      return;
    }

    try {
      const profileUpdateData = {
        username: form.username,
        firstName: form.firstName,
        lastName: form.lastName,
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        state: form.state,
        postalCode: form.zip,
        country: form.country,
        phone: form.phone,
      };

      console.log('💾 Saving profile data:', profileUpdateData);
      const result = await updateProfile(profileUpdateData).unwrap();

      console.log('✅ Profile saved successfully:', result);
      setSaveStatus({ type: 'success', message: 'Profile updated successfully!' });

      // Update original form to reflect saved state
      setOriginalForm({ ...form });

      // Exit edit mode after successful save
      setIsEditMode(false);

      // Auto-hide success message after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error: any) {
      console.error('❌ Failed to save profile:', error);
      const errorMessage = error?.data?.message || 'Failed to save profile. Please try again.';
      setSaveStatus({ type: 'error', message: errorMessage });
    }
  };

  const handleEmailNotificationToggle = async (checked: boolean) => {
    try {
      await updateNotificationSettings({ emailOnNewMessage: checked }).unwrap();
      toast.success(
        checked
          ? 'Email notifications enabled'
          : 'Email notifications disabled'
      );
    } catch (error: any) {
      console.error('Failed to update notification settings:', error);
      toast.error('Failed to update notification preferences. Please try again.');
    }
  };

  const handleTradeNotificationToggle = async (checked: boolean) => {
    try {
      await updateNotificationSettings({ emailOnNewTrade: checked }).unwrap();
      toast.success(
        checked
          ? 'Trade email notifications enabled'
          : 'Trade email notifications disabled'
      );
    } catch (error: any) {
      console.error('Failed to update notification settings:', error);
      toast.error('Failed to update notification preferences. Please try again.');
    }
  };

  const handleShippedNotificationToggle = async (checked: boolean) => {
    try {
      await updateNotificationSettings({ emailOnShipped: checked }).unwrap();
      toast.success(
        checked
          ? 'Shipped email notifications enabled'
          : 'Shipped email notifications disabled'
      );
    } catch (error: any) {
      console.error('Failed to update notification settings:', error);
      toast.error('Failed to update notification preferences. Please try again.');
    }
  };

  const handleSystemMessageNotificationToggle = async (checked: boolean) => {
    try {
      await updateNotificationSettings({ emailOnSystemMessage: checked }).unwrap();
      toast.success(
        checked
          ? 'System message email notifications enabled'
          : 'System message email notifications disabled'
      );
    } catch (error: any) {
      console.error('Failed to update notification settings:', error);
      toast.error('Failed to update notification preferences. Please try again.');
    }
  };

  const handleNewFeaturesNotificationToggle = async (checked: boolean) => {
    try {
      await updateNotificationSettings({ emailOnNewFeatures: checked }).unwrap();
      toast.success(
        checked
          ? 'New features email notifications enabled'
          : 'New features email notifications disabled'
      );
    } catch (error: any) {
      console.error('Failed to update notification settings:', error);
      toast.error('Failed to update notification preferences. Please try again.');
    }
  };

  const handleSoundEffectsToggle = async (checked: boolean) => {
    try {
      await updateNotificationSettings({ soundEnabled: checked }).unwrap();
      toast.success(
        checked
          ? 'Sound effects enabled'
          : 'Sound effects disabled'
      );
    } catch (error: any) {
      console.error('Failed to update sound settings:', error);
      toast.error('Failed to update sound preferences. Please try again.');
    }
  };

  const handleAnimatedBorderToggle = async (checked: boolean) => {
    try {
      await updateAnimatedBorder({ enabled: checked }).unwrap();
      toast.success(
        checked
          ? 'Animated avatar border enabled'
          : 'Animated avatar border disabled'
      );
    } catch (error: any) {
      console.error('Failed to update animated border setting:', error);
      toast.error('Failed to update setting. Please try again.');
    }
  };

  const handlePreferDomesticTradesToggle = async (checked: boolean) => {
    if (!userData?.isElite) {
      setShowDomesticTradesUpsell(true);
      return;
    }
    try {
      await updateTradingPreferences({ preferDomesticTrades: checked }).unwrap();
      toast.success(
        checked
          ? 'Domestic traders will be surfaced first on the Send Cards page'
          : 'Trader ordering reset to default'
      );
    } catch (error: any) {
      console.error('Failed to update trading preferences:', error);
      if (error?.status === 403) {
        setShowDomesticTradesUpsell(true);
        return;
      }
      toast.error('Failed to update preference. Please try again.');
    }
  };

  // Have List Defaults Handler
  const handleReceivedCardsForTradeToggle = async (checked: boolean) => {
    try {
      await updateHaveListDefaults({ receivedCardsForTrade: checked }).unwrap();
      toast.success(
        checked
          ? 'Received cards will default to "For Trade"'
          : 'Received cards will default to "Not for Trade"'
      );
    } catch (error: any) {
      console.error('Failed to update have list defaults:', error);
      toast.error('Failed to update preference. Please try again.');
    }
  };

  // Community Feed Preference Handler
  const handleShowInFeedToggle = async (checked: boolean) => {
    try {
      await updateFeedPreferences({ showInFeed: checked }).unwrap();
      toast.success(
        checked
          ? 'Your trades will appear in the community feed'
          : 'Your trades will be hidden from the community feed'
      );
    } catch (error: any) {
      console.error('Failed to update feed preference:', error);
      toast.error('Failed to update preference. Please try again.');
    }
  };

  // Want List Defaults Handlers
  const handleFoilChange = async (acceptFoil: boolean, acceptNonFoil: boolean) => {
    // Validate at least one is selected
    if (!acceptFoil && !acceptNonFoil) {
      toast.error('At least one foil option must be selected');
      return;
    }

    try {
      await updateWantListDefaults({
        defaultAcceptFoil: acceptFoil,
        defaultAcceptNonFoil: acceptNonFoil,
      }).unwrap();
      toast.success('Preference updated');
    } catch (error: any) {
      console.error('Failed to update want list defaults:', error);
      toast.error('Failed to update preferences. Please try again.');
    }
  };

  const handleConditionChange = async (conditions: string[]) => {
    // Validate at least one is selected
    if (conditions.length === 0) {
      toast.error('At least one condition must be selected');
      return;
    }

    try {
      await updateWantListDefaults({
        defaultConditions: conditions,
      }).unwrap();
      toast.success('Preference updated');
    } catch (error: any) {
      console.error('Failed to update want list defaults:', error);
      toast.error('Failed to update preferences. Please try again.');
    }
  };

  const handleLanguageChange = async (languages: string[]) => {
    // Validate at least one is selected
    if (languages.length === 0) {
      toast.error('At least one language must be selected');
      return;
    }

    try {
      await updateWantListDefaults({
        defaultLanguages: languages,
      }).unwrap();
      toast.success('Preference updated');
    } catch (error: any) {
      console.error('Failed to update want list defaults:', error);
      toast.error('Failed to update preferences. Please try again.');
    }
  };

  const handleBioChange = (value: string) => {
    setBio(value);
    setBioChanged(value !== (profileSettings?.bio || ''));
    setBioHasUrl(containsUrl(value));
  };

  const handleSaveBio = async () => {
    try {
      await updateProfileSettings({ bio: bio || null }).unwrap();
      toast.success('Bio updated successfully');
      setBioChanged(false);
    } catch (error: any) {
      console.error('Failed to update bio:', error);
      const message = error?.data?.message || 'Failed to update bio. Please try again.';
      toast.error(message);
    }
  };

  const validateSocialUrl = (url: string): string => {
    if (!url.trim()) return '';
    if (!url.startsWith('https://')) return 'URL must start with https://';
    try {
      new URL(url);
      return '';
    } catch {
      return 'Invalid URL format';
    }
  };

  const handleSocialUrlChange = (index: number, value: string) => {
    setSocialRows((prev) =>
      prev.map((row, i) => (i === index ? { url: value, error: validateSocialUrl(value) } : row))
    );
    setSocialLinksChanged(true);
  };

  const handleAddSocialRow = () => {
    if (socialRows.length < 5) {
      setSocialRows((prev) => [...prev, { url: '', error: '' }]);
      setSocialLinksChanged(true);
    }
  };

  const handleRemoveSocialRow = (index: number) => {
    setSocialRows((prev) => prev.filter((_, i) => i !== index));
    setSocialLinksChanged(true);
  };

  const handleSaveSocialLinks = async () => {
    // Validate all rows
    const validated = socialRows.map((row) => ({ ...row, error: validateSocialUrl(row.url) }));
    setSocialRows(validated);

    const nonEmpty = validated.filter((r) => r.url.trim());
    if (nonEmpty.some((r) => r.error)) return;

    // Non-Elite users: redirect to Elite page
    if (!userData?.isElite) {
      window.location.href = '/elite?source=social-links';
      return;
    }

    // Elite users: save links
    try {
      await saveSocialLinks({
        links: nonEmpty.map((r, i) => ({ url: r.url.trim(), displayOrder: i + 1 })),
      }).unwrap();
      toast.success('Social links updated!');
      setSocialLinksChanged(false);
    } catch {
      toast.error('Failed to save social links.');
    }
  };

  const handleShowHaveListToggle = async (checked: boolean) => {
    try {
      await updateProfileSettings({ showHaveListOnProfile: checked }).unwrap();
      toast.success(
        checked
          ? 'Have list is now visible on your profile'
          : 'Have list is now hidden on your profile'
      );
    } catch (error: any) {
      console.error('Failed to update profile settings:', error);
      toast.error('Failed to update profile settings. Please try again.');
    }
  };

  const handleShowContactButtonToggle = async (checked: boolean) => {
    try {
      await updateProfileSettings({ showContactButtonOnProfile: checked }).unwrap();
      toast.success(
        checked
          ? 'Contact button is now visible on your profile'
          : 'Contact button is now hidden on your profile'
      );
    } catch (error: any) {
      console.error('Failed to update profile settings:', error);
      toast.error('Failed to update profile settings. Please try again.');
    }
  };

  return (
    <div className="settings-layout">
      <nav className="settings-sidebar" aria-label="Settings navigation">
        {TAB_GROUPS.map((group, gi) => (
          <div key={gi} className="settings-sidebar__group">
            {group.label && (
              <div className="settings-sidebar__group-label">{group.label}</div>
            )}
            {group.tabs.map((tab) => (
              <button
                key={tab.key}
                className={`settings-sidebar__item ${activeTab === tab.key ? 'settings-sidebar__item--active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ))}
      </nav>

      <div className="settings-content-area">
        <div className="settings-mobile-tabs">
          {MOBILE_TABS.map((tab) => (
            <div
              key={tab.key}
              className={`settings-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </div>
          ))}
        </div>

        <div className="settings-body">
        {activeTab === 'Profile' && (
          <div className="settings-form">
            <>
            {/* Profile Summary Card */}
            {profileData && (
              <div className="profile-summary-card">
                {hasChanges() && isEditMode && (
                  <div className="profile-summary-header">
                    <span className="unsaved-indicator">• Unsaved changes</span>
                  </div>
                )}
                <div className="profile-summary-content">
                  <div className="summary-item">
                    <span className="summary-label">Username:</span>
                    <span className="summary-value">{profileData.username || 'Not set'}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Email:</span>
                    <span className="summary-value">{profileData.email}</span>
                  </div>
                  {(profileData.shippingAddress || profileData.shippingFirstName) && (
                    <div className="summary-item">
                      <span className="summary-label">Shipping Address:</span>
                      <span className="summary-value address-multiline">
                        {profileData.shippingAddress ? (
                          <>
                            {profileData.shippingAddress.firstName && profileData.shippingAddress.lastName && (
                              <div>{profileData.shippingAddress.firstName} {profileData.shippingAddress.lastName}</div>
                            )}
                            {profileData.shippingAddress.address1 && (
                              <div>{profileData.shippingAddress.address1}</div>
                            )}
                            {profileData.shippingAddress.address2 && (
                              <div>{profileData.shippingAddress.address2}</div>
                            )}
                            {profileData.shippingAddress.city && profileData.shippingAddress.state && (
                              <div>{profileData.shippingAddress.city}, {profileData.shippingAddress.state} {profileData.shippingAddress.postalCode}</div>
                            )}
                            {profileData.shippingAddress.country && (
                              <div>{profileData.shippingAddress.country}</div>
                            )}
                          </>
                        ) : (
                          <>
                            {profileData.shippingFirstName && profileData.shippingLastName && (
                              <div>{profileData.shippingFirstName} {profileData.shippingLastName}</div>
                            )}
                            {profileData.shippingAddress1 && (
                              <div>{profileData.shippingAddress1}</div>
                            )}
                            {profileData.shippingAddress2 && (
                              <div>{profileData.shippingAddress2}</div>
                            )}
                            {profileData.shippingCity && profileData.shippingState && (
                              <div>{profileData.shippingCity}, {profileData.shippingState} {profileData.shippingPostalCode}</div>
                            )}
                            {profileData.shippingCountry && (
                              <div>{profileData.shippingCountry}</div>
                            )}
                          </>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="summary-item">
                    <span className="summary-label">Phone:</span>
                    <span className="summary-value" style={!profileData.phoneNumber ? { color: '#888' } : undefined}>
                      {profileData.phoneNumber || 'No phone number added'}
                      {!profileData.phoneNumber && (
                        <button className="summary-value-add-link" onClick={() => setIsPhoneModalOpen(true)}>
                          Add
                        </button>
                      )}
                    </span>
                  </div>
                </div>
                {!isEditMode && (
                  <div className="profile-edit-button-container">
                    <MagicButton
                      label="Edit Profile"
                      color="purple"
                      onPress={handleEdit}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Empty State Message */}
            {!isLoadingProfile && !profileData?.username && !profileData?.shippingAddress?.firstName && !profileData?.shippingFirstName && (
              <div className="empty-state-message">
                <p>👋 Welcome! Please complete your profile information below.</p>
              </div>
            )}
            
            {/* Edit Mode Form Fields */}
            {isEditMode && (
              <>
            {/* Username */}
            <h3 className="settings-section-title">Username</h3>
            <TextInputField
              placeholder={form.username ? "Enter username" : "Choose a unique username"}
              value={form.username}
              onChange={(val) => handleChange('username', val)}
            />
            {validationErrors.username && (
              <div className="validation-error">{validationErrors.username}</div>
            )}

            {/* Shipping Address */}
            <h3 className="settings-section-title">Shipping Address</h3>
            <div className="settings-row">
              <div className="settings-field-container">
                <TextInputField
                  placeholder={form.firstName ? "First Name" : "Enter your first name"}
                  value={form.firstName}
                  onChange={(val) => handleChange('firstName', val)}
                />
                {validationErrors.firstName && (
                  <div className="validation-error">{validationErrors.firstName}</div>
                )}
              </div>
              <div className="settings-field-container">
                <TextInputField
                  placeholder={form.lastName ? "Last Name" : "Enter your last name"}
                  value={form.lastName}
                  onChange={(val) => handleChange('lastName', val)}
                />
                {validationErrors.lastName && (
                  <div className="validation-error">{validationErrors.lastName}</div>
                )}
              </div>
            </div>

            <TextInputField
              placeholder={form.address1 ? "Address Line 1" : "Street address or P.O. box"}
              value={form.address1}
              onChange={(val) => handleChange('address1', val)}
            />
            {validationErrors.address1 && (
              <div className="validation-error">{validationErrors.address1}</div>
            )}
            <TextInputField
              placeholder="Address Line 2 (Optional)"
              value={form.address2}
              onChange={(val) => handleChange('address2', val)}
            />

            <div className="settings-row">
              <div className="settings-field-container">
                <TextInputField
                  placeholder={form.city ? "City" : "Enter your city"}
                  value={form.city}
                  onChange={(val) => handleChange('city', val)}
                />
                {validationErrors.city && (
                  <div className="validation-error">{validationErrors.city}</div>
                )}
              </div>
              <div className="settings-field-container">
                <TextInputField
                  placeholder={form.state ? "State / Province" : "e.g., CA, NY, TX"}
                  value={form.state}
                  onChange={(val) => handleChange('state', val)}
                />
                {validationErrors.state && (
                  <div className="validation-error">{validationErrors.state}</div>
                )}
              </div>
            </div>

            <div className="settings-row">
              <div className="settings-field-container">
                <TextInputField
                  placeholder={form.zip ? "ZIP / Postal Code" : "e.g., 10001"}
                  value={form.zip}
                  onChange={(val) => handleChange('zip', val)}
                />
                {validationErrors.zip && (
                  <div className="validation-error">{validationErrors.zip}</div>
                )}
              </div>
              <div className="settings-field-container">
                <CountrySelect
                  value={form.country}
                  onChange={(val) => handleChange('country', val)}
                  error={validationErrors.country}
                />
              </div>
            </div>

            {/* Contact Info */}
            <h3 className="settings-section-title">Contact Info</h3>
            <div className="email-field-wrapper">
              <div className="read-only-field" style={{ flex: 1 }}>
                <TextInputField
                  placeholder="Email"
                  value={form.email}
                  onChange={() => {}} // Read-only
                />
                <div className="read-only-overlay" />
              </div>
              <button
                className="email-change-button"
                onClick={() => setIsEmailModalOpen(true)}
                type="button"
              >
                Change
              </button>
            </div>
            {/* Email verification status */}
            {userData && !userData.emailVerified && (
              <div className="email-verification-notice">
                <span className="warning-icon">⚠️</span>
                <span>Email not verified</span>
                <button
                  className="resend-verification-button"
                  onClick={async () => {
                    try {
                      if (!userData?.email) {
                        toast.error('Unable to send verification email. Please try again.');
                        return;
                      }
                      await resendVerification({ email: userData.email }).unwrap();
                      toast.success('Verification email sent! Please check your inbox.');
                    } catch (error) {
                      toast.error('Failed to send verification email. Please try again.');
                    }
                  }}
                  type="button"
                >
                  Resend Verification
                </button>
              </div>
            )}
            {userData && userData.emailVerified && (
              <div className="email-verified-notice">
                <span className="verified-icon">✅</span>
                <span>Email verified</span>
              </div>
            )}
            {emailChangeStatus?.pendingEmail && (
              <div className="pending-email-notice">
                <span className="pending-icon">⏳</span>
                Pending change to: {emailChangeStatus.pendingEmail}
              </div>
            )}
            <div className="phone-field-wrapper">
              <div className="read-only-field" style={{ flex: 1 }}>
                <TextInputField
                  placeholder={form.phone ? "Phone" : "Phone number (Optional)"}
                  value={form.phone}
                  onChange={() => {}} // Read-only
                />
                <div className="read-only-overlay" />
              </div>
              <button
                className="phone-change-button"
                onClick={() => setIsPhoneModalOpen(true)}
                type="button"
              >
                {userData?.phoneVerified ? 'Change' : 'Verify Now'}
              </button>
            </div>
            {/* Phone verification status */}
            {userData && !userData.phoneVerified && (
              <div className="phone-verification-notice">
                <span className="warning-icon">⚠️</span>
                <span>Phone not verified</span>
              </div>
            )}
            {userData && userData.phoneVerified && (
              <div className="phone-verified-notice">
                <span className="verified-icon">✅</span>
                <span>Phone verified</span>
              </div>
            )}
              </>
            )}

            {/* Save Status Messages */}
            {isLoadingProfile && (
              <div className="loading-message">Loading profile data...</div>
            )}
            {profileError && (
              <StatusMessage 
                type="error" 
                message="Failed to load profile data. Please refresh the page." 
              />
            )}
            {saveStatus && (
              <StatusMessage 
                type={saveStatus.type} 
                message={saveStatus.message} 
              />
            )}

            {/* Action Buttons */}
            {isEditMode && (
              <div className="settings-button-container">
                <MagicButton 
                  label="Cancel" 
                  color="dark-grey" 
                  onPress={handleCancel}
                  isDisabled={isSaving}
                />
                <MagicButton 
                  label={isSaving ? "Saving..." : "Save Changes"} 
                  color="purple" 
                  onPress={handleSave}
                  isDisabled={!hasChanges() || isSaving || isLoadingProfile}
                />
              </div>
            )}
            </>
          </div>
        )}
        {activeTab === 'Public' && (
          <div className="settings-form">
            <h3 className="settings-section-title">Profile URL</h3>
            <div className="settings-profile-url-section">
              <div className="settings-profile-url-row">
                <span className="settings-profile-url-label">Default URL</span>
                <div className="settings-profile-url-value">
                  <span className="settings-profile-url-text">trademagic.gg/profile/u/{userData?.username}</span>
                  <button
                    className="settings-profile-url-copy"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(`https://trademagic.gg/profile/u/${userData?.username}`);
                        toast.success('Profile link copied!');
                      } catch {
                        toast.error('Failed to copy link');
                      }
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
              {userData?.isElite && userData?.customSlug && (
                <div className="settings-profile-url-row">
                  <span className="settings-profile-url-label">Custom Elite URL</span>
                  <div className="settings-profile-url-value">
                    <span className="settings-profile-url-text settings-profile-url-elite">trademagic.gg/u/{userData.customSlug}</span>
                    <button
                      className="settings-profile-url-copy"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(`https://trademagic.gg/u/${userData.customSlug}`);
                          toast.success('Custom URL copied!');
                        } catch {
                          toast.error('Failed to copy link');
                        }
                      }}
                    >
                      Copy
                    </button>
                  </div>
                  <a href="/elite" className="settings-profile-url-manage">Manage on Elite page</a>
                </div>
              )}
              {!userData?.isElite && (
                <p className="settings-profile-url-upsell">
                  Want a custom profile URL? <a href="/elite?source=custom-slug">Upgrade to Elite</a>
                </p>
              )}
            </div>

            <div
              className="notification-setting-item notification-setting-item--with-preview"
              style={{ marginTop: '32px' }}
            >
              <div className="notification-setting-preview">
                <AvatarWithFlag
                  avatarUrl={userData?.avatarUrl}
                  username={userData?.username || ''}
                  size={48}
                  isElite={true}
                  showAnimatedBorder={true}
                  borderColor1={userData?.borderColor1 || '#FFD700'}
                  borderColor2={userData?.borderColor2 || '#9B59B6'}
                />
              </div>
              <div className="notification-setting-label">
                Animated Avatar Border
                <img
                  src={AssetUrls.icons.elite.badge}
                  alt="Elite feature"
                  className="settings-elite-lock-badge"
                />
              </div>
              <div className="notification-setting-description">
                Display an animated glowing border around your avatar across the platform
              </div>
              <div className="notification-setting-toggle notification-setting-toggle--with-button">
                <button
                  className="settings-customize-border-btn"
                  onClick={() => setIsBorderColorModalOpen(true)}
                >
                  Preview
                </button>
                <ToggleSwitch
                  checked={userData?.isElite ? (userData?.showAnimatedBorder ?? true) : false}
                  onChange={userData?.isElite ? handleAnimatedBorderToggle : () => setShowEliteProfileUpsell(true)}
                />
              </div>
            </div>
            <AvatarBorderPreviewBanner
              userId={userData?.id}
              visible={!userData?.isElite && hasSavedBorder}
            />

            <EliteUpsellPrompt
              isOpen={showEliteProfileUpsell}
              onClose={() => setShowEliteProfileUpsell(false)}
              source="profile-customization"
              title="Profile Customization is an Elite Feature"
              body="Make your profile yours — animated avatar borders, custom gradient colors, and more."
              emojiRow={
                <>
                  <span className="elite-upsell-modal__emoji-pill">
                    <span>&#10024;</span> 3
                  </span>
                  <span className="elite-upsell-modal__emoji-pill">
                    <span>&#127912;</span> 2
                  </span>
                  <span className="elite-upsell-modal__emoji-pill">
                    <span>&#128142;</span> 1
                  </span>
                </>
              }
              learnMoreLabel="Upgrade to Elite"
            />

            <h3 className="settings-section-title" style={{ marginTop: '32px' }}>Bio</h3>
            <p className="settings-section-description">
              Tell other traders about yourself. This will be displayed on your public profile.
            </p>
            <textarea
              className="settings-bio-textarea"
              placeholder="Tell other traders about yourself..."
              value={bio}
              onChange={(e) => handleBioChange(e.target.value)}
              maxLength={500}
              rows={4}
            />
            <CharacterCounter
              currentLength={bio.length}
              maxLength={500}
              warningThreshold={450}
            />
            {bioHasUrl && !userData?.isElite && (
              <div className="settings-bio-url-warning">
                Want to share links on your profile?{' '}
                <a href="/elite?source=bio-url">Elite members</a> can add up to 5 social profile links with auto-detected platform icons.
              </div>
            )}
            <div className="settings-bio-save">
              <MagicButton
                label={isUpdatingProfileSettings ? "Saving..." : "Save Bio"}
                color="purple"
                onPress={handleSaveBio}
                isDisabled={!bioChanged || isUpdatingProfileSettings || isLoadingProfileSettings || (bioHasUrl && !userData?.isElite)}
              />
            </div>

            <div className="settings-social-links-section">
              <h3 className="settings-section-title" style={{ marginTop: '40px' }}>
                Social Links
                <img src={AssetUrls.icons.elite.badge} alt="Elite" className="settings-social-links-elite-badge" />
              </h3>
              <p className="settings-section-description">
                Add up to 5 links to your social profiles. Platform icons are auto-detected.
              </p>
              <div className="settings-social-links-list">
                {socialRows.map((row, index) => {
                  const platform = row.url.trim() ? detectPlatform(row.url) : null;
                  const iconTuple = platform ? PLATFORM_ICON_MAP[platform] : null;
                  const label = platform ? PLATFORM_LABEL_MAP[platform] : null;
                  return (
                    <div key={index} className="settings-social-link-row">
                      <div className="settings-social-link-icon">
                        {iconTuple ? (
                          <FontAwesomeIcon icon={iconTuple} title={label || undefined} />
                        ) : (
                          <FontAwesomeIcon icon={['fas', 'link']} className="settings-social-link-icon--empty" />
                        )}
                      </div>
                      <div className="settings-social-link-input-wrapper">
                        <input
                          type="text"
                          className={`settings-social-link-input${row.error ? ' settings-social-link-input--error' : ''}`}
                          value={row.url}
                          onChange={(e) => handleSocialUrlChange(index, e.target.value)}
                          placeholder="https://twitter.com/yourname"
                        />
                        {row.error && <span className="settings-social-link-error">{row.error}</span>}
                      </div>
                      <button
                        className="settings-social-link-remove"
                        onClick={() => handleRemoveSocialRow(index)}
                        aria-label="Remove link"
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
              {socialRows.length < 5 && (
                <button className="settings-social-link-add" onClick={handleAddSocialRow}>
                  + Add Link
                </button>
              )}
              <div className="settings-social-links-save">
                <MagicButton
                  label={isSavingLinks ? "Saving..." : "Save Links"}
                  color="purple"
                  onPress={handleSaveSocialLinks}
                  isDisabled={isSavingLinks || !socialLinksChanged}
                />
              </div>
            </div>

            <h3 className="settings-section-title" style={{ marginTop: '40px' }}>Privacy</h3>
            <div className="notification-setting-item">
              <div className="notification-setting-label">Show Have List on Profile</div>
              <div className="notification-setting-description">
                Allow other users to see your Have List on your public profile
              </div>
              <div className="notification-setting-toggle">
                <ToggleSwitch
                  checked={profileSettings?.showHaveListOnProfile ?? false}
                  onChange={handleShowHaveListToggle}
                  disabled={isLoadingProfileSettings || isUpdatingProfileSettings}
                />
              </div>
            </div>
            <div className="notification-setting-item">
              <div className="notification-setting-label">Show Contact Button on Profile</div>
              <div className="notification-setting-description">
                Allow others to contact me from my public profile
              </div>
              <div className="notification-setting-toggle">
                <ToggleSwitch
                  checked={profileSettings?.showContactButtonOnProfile ?? false}
                  onChange={handleShowContactButtonToggle}
                  disabled={isLoadingProfileSettings || isUpdatingProfileSettings}
                />
              </div>
            </div>
          </div>
        )}
        {activeTab === 'Preferences' && (
          <div className="settings-form settings-preferences-container">
            <PreferenceSection
              title="Version Defaults - Want List"
              description="Set default card versions for your want list. These versions will be automatically added when available."
            >
              <div className="version-defaults-container">
                <div className="version-default-item">
                  <label className="version-default-label">Foil</label>
                  <InlineFoilPills
                    acceptFoil={wantListDefaults?.defaultAcceptFoil ?? true}
                    acceptNonFoil={wantListDefaults?.defaultAcceptNonFoil ?? true}
                    onChange={handleFoilChange}
                    disabled={isLoadingWantListDefaults || isUpdatingWantListDefaults}
                  />
                </div>

                <div className="version-default-item">
                  <label className="version-default-label">Condition</label>
                  <InlineConditionPills
                    value={wantListDefaults?.defaultConditions ?? ['NM', 'LP', 'MP']}
                    onChange={handleConditionChange}
                    disabled={isLoadingWantListDefaults || isUpdatingWantListDefaults}
                  />
                </div>

                <div className="version-default-item">
                  <button
                    className="language-accordion-header"
                    onClick={() => setIsLanguageExpanded(!isLanguageExpanded)}
                    type="button"
                  >
                    <label className="version-default-label">Language</label>
                    <span className="language-accordion-summary">
                      {(() => {
                        const selected = wantListDefaults?.defaultLanguages ?? ['EN'];
                        const names = selected.map(code => LANGUAGES.find(l => l.code === code)?.name || code);
                        if (names.length <= 2) return names.join(', ');
                        return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
                      })()}
                    </span>
                    <span className={`language-accordion-chevron ${isLanguageExpanded ? 'expanded' : ''}`}>
                      &#9660;
                    </span>
                  </button>
                  {isLanguageExpanded && (
                    <div className="language-pills-container">
                      {LANGUAGES.map((lang) => {
                        const isActive = (wantListDefaults?.defaultLanguages ?? ['EN']).includes(lang.code);
                        return (
                          <button
                            key={lang.code}
                            className={`filter-pill ${isActive ? 'active' : ''}`}
                            onClick={() => {
                              const currentLanguages = wantListDefaults?.defaultLanguages ?? ['EN'];
                              const newLanguages = isActive
                                ? currentLanguages.filter((l) => l !== lang.code)
                                : [...currentLanguages, lang.code];
                              handleLanguageChange(newLanguages);
                            }}
                            disabled={isLoadingWantListDefaults || isUpdatingWantListDefaults}
                          >
                            <span>{lang.name}</span>
                            {isActive && (
                              <img
                                src={AssetUrls.icons.dropdowns.close}
                                alt="close"
                                className="filter-pill-close"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </PreferenceSection>

            <PreferenceSection title="Have List Defaults">
              <div className="notification-setting-item">
                <div className="notification-setting-label">Received Cards Default to For Trade</div>
                <div className="notification-setting-description">
                  When you receive cards in trades, add them as available for trading
                </div>
                <div className="notification-setting-toggle">
                  <ToggleSwitch
                    checked={haveListDefaults?.receivedCardsForTrade ?? false}
                    onChange={handleReceivedCardsForTradeToggle}
                    disabled={isLoadingHaveListDefaults || isUpdatingHaveListDefaults}
                  />
                </div>
              </div>
            </PreferenceSection>

            <PreferenceSection title="Community Feed">
              <div className="notification-setting-item">
                <div className="notification-setting-label">Show my trades in the community feed</div>
                <div className="notification-setting-description">
                  When enabled, your trades appear in the public activity feed visible to all members.
                </div>
                <div className="notification-setting-toggle">
                  <ToggleSwitch
                    checked={feedPreferences?.showInFeed ?? true}
                    onChange={handleShowInFeedToggle}
                    disabled={isLoadingFeedPreferences || isUpdatingFeedPreferences}
                  />
                </div>
              </div>
            </PreferenceSection>

            <PreferenceSection title="Trading Preferences">
              <div className="notification-setting-item">
                <div className="notification-setting-label">
                  Prefer Domestic Trades
                  <img
                    src={AssetUrls.icons.elite.badge}
                    alt="Elite feature"
                    className="settings-elite-lock-badge"
                  />
                </div>
                <div className="notification-setting-description">
                  Surface same-country traders first on the Send Cards page.
                </div>
                <div className="notification-setting-toggle">
                  <ToggleSwitch
                    checked={userData?.isElite ? (userData?.preferDomesticTrades ?? false) : false}
                    onChange={handlePreferDomesticTradesToggle}
                    disabled={userData?.isElite && isUpdatingTradingPrefs}
                  />
                </div>
              </div>
              <EliteUpsellPrompt
                isOpen={showDomesticTradesUpsell}
                onClose={() => setShowDomesticTradesUpsell(false)}
                source="prefer-domestic-trades"
                title="Prefer Domestic Trades is an Elite Feature"
                body="Prioritize traders in your country without lifting a finger — set it once, benefit every session."
                emojiRow={
                  <>
                    <span className="elite-upsell-modal__emoji-pill">
                      <span>&#127758;</span> 3
                    </span>
                    <span className="elite-upsell-modal__emoji-pill">
                      <span>&#9992;&#65039;</span> 2
                    </span>
                    <span className="elite-upsell-modal__emoji-pill">
                      <span>&#129309;</span> 1
                    </span>
                  </>
                }
                learnMoreLabel="Upgrade to Elite"
              />
            </PreferenceSection>

            <PreferenceSection title="Sound Effects">
              <div className="notification-setting-item">
                <div className="notification-setting-label">Point Balance Sounds</div>
                <div className="notification-setting-description">
                  Play coin sounds when your point balance changes
                </div>
                <div className="notification-setting-toggle">
                  <ToggleSwitch
                    checked={notificationSettings?.soundEnabled ?? true}
                    onChange={handleSoundEffectsToggle}
                    disabled={isLoadingNotifications || isUpdatingNotifications}
                  />
                </div>
              </div>
            </PreferenceSection>

          </div>
        )}
        {activeTab === 'Email' && (
          <div className="settings-form settings-preferences-container">
            <PreferenceSection title="Email Notifications">
              <div className="notification-setting-item">
                <div className="notification-setting-label">Message Notifications</div>
                <div className="notification-setting-description">
                  Receive an email when someone sends you a message
                </div>
                <div className="notification-setting-toggle">
                  <ToggleSwitch
                    checked={notificationSettings?.emailOnNewMessage ?? true}
                    onChange={handleEmailNotificationToggle}
                    disabled={isLoadingNotifications || isUpdatingNotifications}
                  />
                </div>
              </div>
              <div className="notification-setting-item">
                <div className="notification-setting-label">Trade Notifications</div>
                <div className="notification-setting-description">
                  Email me when someone sends me cards
                </div>
                <div className="notification-setting-toggle">
                  <ToggleSwitch
                    checked={notificationSettings?.emailOnNewTrade ?? true}
                    onChange={handleTradeNotificationToggle}
                    disabled={isLoadingNotifications || isUpdatingNotifications}
                  />
                </div>
              </div>
              <div className="notification-setting-item">
                <div className="notification-setting-label">Shipped Notifications</div>
                <div className="notification-setting-description">
                  Receive an email when cards you're expecting are marked as shipped
                </div>
                <div className="notification-setting-toggle">
                  <ToggleSwitch
                    checked={notificationSettings?.emailOnShipped ?? true}
                    onChange={handleShippedNotificationToggle}
                    disabled={isLoadingNotifications || isUpdatingNotifications}
                  />
                </div>
              </div>
              <div className="notification-setting-item">
                <div className="notification-setting-label">System Message Notifications</div>
                <div className="notification-setting-description">
                  Receive an email when TradeMagic sends you a system message
                </div>
                <div className="notification-setting-toggle">
                  <ToggleSwitch
                    checked={notificationSettings?.emailOnSystemMessage ?? true}
                    onChange={handleSystemMessageNotificationToggle}
                    disabled={isLoadingNotifications || isUpdatingNotifications}
                  />
                </div>
              </div>
              <div className="notification-setting-item">
                <div className="notification-setting-label">New Features</div>
                <div className="notification-setting-description">
                  Receive an email when new features are published on TradeMagic
                </div>
                <div className="notification-setting-toggle">
                  <ToggleSwitch
                    checked={notificationSettings?.emailOnNewFeatures ?? true}
                    onChange={handleNewFeaturesNotificationToggle}
                    disabled={isLoadingNotifications || isUpdatingNotifications}
                  />
                </div>
              </div>
            </PreferenceSection>
          </div>
        )}
        {activeTab === 'Emoji' && (
          <div className="settings-form">
            <CustomEmojiTab />
          </div>
        )}

        {activeTab === 'Billing' && (
          <div className="settings-form">
            <BillingTab />
          </div>
        )}
      </div>
      </div>

      {/* Email Change Modal */}
      <EmailChangeModal
        isOpen={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false);
          refetchEmailStatus();
        }}
        currentEmail={form.email}
      />

      {/* Mobile Verification Modal */}
      <MobileVerificationModal
        isOpen={isPhoneModalOpen}
        onClose={() => {
          setIsPhoneModalOpen(false);
        }}
        currentPhone={userData?.phoneNumber}
        onVerificationSuccess={() => {
          // Refresh user data to get updated phone verification status
          window.location.reload();
        }}
      />

      <BorderColorPickerModal
        isOpen={isBorderColorModalOpen}
        onClose={() => setIsBorderColorModalOpen(false)}
        currentColor1={userData?.borderColor1 || '#FFD700'}
        currentColor2={userData?.borderColor2 || '#9B59B6'}
        avatarUrl={userData?.avatarUrl}
        username={userData?.username}
        onAfterSave={() => {
          if (!userData?.isElite && userData?.id != null) {
            localStorage.setItem(`avatarBorderPreview.hasSaved.${userData.id}`, 'true');
            setHasSavedBorder(true);
          }
        }}
      />
    </div>
  );
}